import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { formatAmount } from '../Shared/Formatters';

/**
 * TenantPeriodTable — Sprint 3.9
 *
 * Single table component that renders both views via `mode`:
 *   - mode="mom"  → month-on-month % change within each year
 *   - mode="yoy"  → year-over-year change per month + footer totals
 *
 * Replaces MonthOnMonthTable.jsx + GrowthAnalysis.jsx (≈250 LoC dedup).
 */

function PctCell({ value }) {
    const { t } = useTranslation();
    if (value === null || value === undefined || value === '' || value === '-') {
        return <span className="v3-td-pct zero">—</span>;
    }
    const n = Number(value);
    if (isNaN(n)) return <span className="v3-td-pct zero">—</span>;
    const cls = n > 0 ? 'pos' : n < 0 ? 'neg' : 'zero';
    const arrow = n > 0 ? '▲' : n < 0 ? '▼' : '●';
    return (
        <span className={`v3-td-pct ${cls}`} aria-label={n > 0 ? t('{{value}} percent up', { value: n.toFixed(2) }) : n < 0 ? t('{{value}} percent down', { value: n.toFixed(2) }) : t('{{value}} percent flat', { value: n.toFixed(2) })}>
            {arrow} {n > 0 ? '+' : ''}{n.toFixed(2)}%
        </span>
    );
}

function fmtSalesMoM(v) {
    if (v === null || v === undefined) return '—';
    // MoM endpoint divides by 1e6 server-side, so we re-multiply for display
    // through formatAmount, then suffix M ourselves to make the unit explicit.
    const n = Number(v);
    if (isNaN(n)) return '—';
    if (n === 0) return '0';
    return `${formatAmount(n)} M`;
}

function fmtSalesYoY(v) {
    if (v === null || v === undefined) return '—';
    const n = Number(v);
    if (isNaN(n)) return '—';
    return formatAmount(n);
}

export default function TenantPeriodTable({ mode, data, loading, error, onRetry }) {
    const { t } = useTranslation();
    const isMoM = mode === 'mom';
    const title = isMoM ? 'Month-on-Month Sales' : 'Year-over-Year Growth';

    const rows = useMemo(() => {
        if (!data) return [];
        const current = Array.isArray(data.currentYearSales) ? data.currentYearSales : [];
        const previous = Array.isArray(data.previousYearSales) ? data.previousYearSales : [];

        // For MoM: pair by index, show both years' MoM%.
        // For YoY: pair by index, show YOYChange (computed server-side on current).
        const len = Math.max(current.length, previous.length);
        const out = [];
        for (let i = 0; i < len; i++) {
            out.push({
                month: current[i]?.month || previous[i]?.month || t('Month {{num}}', { num: i + 1 }),
                current: current[i]?.totalSales ?? null,
                currentChange: current[i]?.percentageChange ?? null,
                previous: previous[i]?.totalSales ?? null,
                previousChange: previous[i]?.percentageChange ?? null,
                yoy: current[i]?.YOYChange ?? null,
            });
        }
        return out;
    }, [data, t]);

    const fmtSales = isMoM ? fmtSalesMoM : fmtSalesYoY;

    return (
        <div className="v3-td-card v3-td-section">
            <h3 className="v3-td-card-title">{t(title)}</h3>

            {error ? (
                <div className="v3-td-error">
                    {t('Unable to load {{title}}', { title: t(title).toLowerCase() })} — {error}
                    <button type="button" onClick={onRetry}>{t('Retry')}</button>
                </div>
            ) : loading ? (
                <div>
                    {Array.from({ length: 4 }).map((_, i) => (
                        <div key={i} className="v3-td-skeleton" style={{ height: 30, margin: '6px 0' }} />
                    ))}
                </div>
            ) : rows.length === 0 ? (
                <div className="v3-td-empty" style={{ margin: '12px 0 0', padding: 20 }}>
                    {t('No data for this period.')}
                </div>
            ) : (
                <div className="v3-td-table-wrap">
                    <table className="v3-td-table">
                        <thead>
                            <tr>
                                <th>{t('Month')}</th>
                                {isMoM ? (
                                    <>
                                        <th>{t('Prev-Year Sales')}</th>
                                        <th>{t('Prev-Year MoM')}</th>
                                        <th>{t('Current-Year Sales')}</th>
                                        <th>{t('Current-Year MoM')}</th>
                                    </>
                                ) : (
                                    <>
                                        <th>{t('Prev-Year Sales')}</th>
                                        <th>{t('Current-Year Sales')}</th>
                                        <th>{t('YoY Change')}</th>
                                    </>
                                )}
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((r, i) => (
                                <tr key={r.month + i}>
                                    <td>{r.month}</td>
                                    {isMoM ? (
                                        <>
                                            <td>{fmtSales(r.previous)}</td>
                                            <td><PctCell value={r.previousChange} /></td>
                                            <td>{fmtSales(r.current)}</td>
                                            <td><PctCell value={r.currentChange} /></td>
                                        </>
                                    ) : (
                                        <>
                                            <td>{fmtSales(r.previous)}</td>
                                            <td>{fmtSales(r.current)}</td>
                                            <td><PctCell value={r.yoy} /></td>
                                        </>
                                    )}
                                </tr>
                            ))}
                        </tbody>
                        {!isMoM && (data.totalCurrentYearSales !== undefined || data.totalPreviousYearSales !== undefined) && (
                            <tfoot>
                                <tr>
                                    <td>{t('Total')}</td>
                                    <td>{fmtSales(data.totalPreviousYearSales)}</td>
                                    <td>{fmtSales(data.totalCurrentYearSales)}</td>
                                    <td>
                                        <PctCell
                                            value={
                                                Number(data.totalPreviousYearSales) > 0
                                                    ? ((Number(data.totalCurrentYearSales) - Number(data.totalPreviousYearSales))
                                                        / Number(data.totalPreviousYearSales)) * 100
                                                    : null
                                            }
                                        />
                                    </td>
                                </tr>
                            </tfoot>
                        )}
                    </table>
                </div>
            )}
        </div>
    );
}
